import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, defer, of, throwError } from 'rxjs';

import { mockResponses } from './mock-data';
import { MockResponse, ResponseStateEnum } from './mock.models';

function matchesPath(path: MockResponse['path'], pathname: string): boolean {
  return typeof path === 'string' ? path === pathname : path.test(pathname);
}

function matchesQuery(query: MockResponse['query'], searchParams: URLSearchParams): boolean {
  return Object.entries(query ?? {}).every(([key, value]) => searchParams.get(key) === value);
}

function queryKeyCount(mockResponse: MockResponse | undefined): number {
  return mockResponse ? Object.keys(mockResponse.query ?? {}).length : -1;
}

@Injectable()
export class MockResponseInterceptor implements HttpInterceptor {
  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const mockResponse = this.findMockResponse(request);

    if (mockResponse?.state === ResponseStateEnum.SUCCESS) {
      const { status, body } = mockResponse.response;
      return defer(() => of(new HttpResponse({ status, body: typeof body === 'function' ? body(request) : body })));
    }

    if (mockResponse?.state === ResponseStateEnum.ERROR) {
      return throwError(mockResponse.error);
    }

    return next.handle(request);
  }

  private findMockResponse(request: HttpRequest<unknown>): MockResponse | undefined {
    const { pathname, searchParams } = new URL(request.url);

    return mockResponses
      .filter(
        ({ path, method, query }) =>
          matchesPath(path, pathname) && method === request.method && matchesQuery(query, searchParams),
      )
      .reduce<MockResponse | undefined>(
        (best, candidate) => (queryKeyCount(candidate) > queryKeyCount(best) ? candidate : best),
        undefined,
      );
  }
}
